import { useEffect, useMemo, useState } from "react";
import { CalendarEvent } from "@/shared/models/economic-calendar/CalendarEvent";
import { useGetCalendarEvents } from "@/shared/api/economic-calendar/useGetCalendarEvents";

interface EconomicCalendarCountdownProps {
  onClick: (upcomingEvent: CalendarEvent) => void;
}

const formatTimeFromMilliseconds = (timeInMillis: number) => ({
  hours: Math.floor(timeInMillis / 1000 / 60 / 60),
  minutes: Math.floor((timeInMillis / 1000 / 60) % 60),
  seconds: Math.floor((timeInMillis / 1000) % 60),
  totalInMillis: timeInMillis,
});

const computeUpcomingEvent = (
  events: CalendarEvent[]
): CalendarEvent | undefined => {
  if (!events || events.length === 0) return undefined;

  const now = new Date();

  const upcomingEvents = events
    .filter((event) => {
      const eventTime = new Date(event.occurs_at);
      return !isNaN(eventTime.getTime()) && eventTime.getTime() > now.getTime();
    })
    .sort((a, b) => {
      const timeA = new Date(a.occurs_at).getTime();
      const timeB = new Date(b.occurs_at).getTime();
      return timeA - timeB;
    });

  return upcomingEvents.length > 0 ? upcomingEvents[0] : undefined;
};

const computeTimeToEvent = (event: CalendarEvent): number => {
  const eventTime = new Date(event.occurs_at);

  if (isNaN(eventTime.getTime())) return 0;

  const diff = eventTime.getTime() - new Date().getTime();

  if (diff < 0) return 0;

  return diff;
};

const EconomicCalendarCountdown = (props: EconomicCalendarCountdownProps) => {
  const { data: calendarEvents, isLoading } = useGetCalendarEvents(new Date());

  // Stores the next upcoming calendar event
  const [upcomingEvent, setUpcomingEvent] = useState<CalendarEvent>();

  // Time remaining until the upcoming event in milliseconds
  const [timeToUpcomingEvent, setTimeToUpcomingEvent] = useState(0);

  // Derived state: formatted time breakdown (hours, minutes, seconds) for rendering
  const formattedTimeToUpcomingEvent = useMemo(() => {
    return formatTimeFromMilliseconds(timeToUpcomingEvent);
  }, [timeToUpcomingEvent]);

  // Find and set the next upcoming event when calendar data changes
  useEffect(() => {
    if (!calendarEvents) return;

    const nextUpcomingEvent = computeUpcomingEvent(calendarEvents);

    setUpcomingEvent(nextUpcomingEvent);
  }, [calendarEvents]);

  // Update countdown timer every second and switch to next event when current expires
  useEffect(() => {
    if (!upcomingEvent) return;

    const timeToUpcomingEvent = computeTimeToEvent(upcomingEvent);
    setTimeToUpcomingEvent(timeToUpcomingEvent);

    const interval = setInterval(() => {
      if (upcomingEvent) {
        const timeToUpcomingEvent = computeTimeToEvent(upcomingEvent);

        if (timeToUpcomingEvent <= 0) {
          const nextUpcomingEvent = computeUpcomingEvent(calendarEvents || []);
          setUpcomingEvent(nextUpcomingEvent);
        } else {
          setTimeToUpcomingEvent(timeToUpcomingEvent);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [upcomingEvent, calendarEvents]);

  if (isLoading) return <p className="text-gray-500 dark:text-gray-400 text-xs">Loading...</p>;

  return upcomingEvent ? (
    <div className="text-gray-700 dark:text-gray-300 flex flex-col items-center">
      <button
        className="rounded-none w-full text-1.5xs bg-white dark:bg-gray-900 hover:border-squawk-blue hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-400  focus:outline-none"
        onClick={() => props.onClick(upcomingEvent)}
      >
        Next event:{" "}
        <span className="text-squawk-blue ">
          <span className="inline-block w-[2ch] text-center ">
            {formattedTimeToUpcomingEvent.hours.toString().padStart(2, "0")}
          </span>
          :
          <span className="inline-block w-[2ch] text-center ">
            {formattedTimeToUpcomingEvent.minutes.toString().padStart(2, "0")}
          </span>
          :
          <span className="inline-block w-[2ch] text-center ">
            {formattedTimeToUpcomingEvent.seconds.toString().padStart(2, "0")}
          </span>
        </span>
      </button>
    </div>
  ) : (
    <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
      No upcoming events found.
    </p>
  );
};

export default EconomicCalendarCountdown;
